// src/pages/BetalService.jsx
// /betal/:ref — hit peker QR-koden på servicelappen.
//  - Ikke innlogget (typisk en kunde som skanner lappen): en enkel side uten
//    kundeopplysninger og uten API-kall. Referansenummeret gir ingen tilgang.
//  - Innlogget ansatt: kvitteringsoversikt i en dialog, med knapp for å sende
//    beløpet til terminalen. Er servicen allerede betalt (Vipps-lenke, terminal
//    eller huket av i butikk), vises det i stedet.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { isAuthenticated } from "../auth";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TERMINAL_KEY = "betalTerminal";
const POLL_MS = 1500;

const nok = new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK", minimumFractionDigits: 0, maximumFractionDigits: 2 });
const fmtKr = (n) => nok.format(Number(n) || 0);
const fmtDT = (d) => (d ? new Date(d).toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" }) : "");

const VIA_TEKST = {
  vipps: "med Vipps (betalingslenke)",
  terminal: "på terminal i butikk",
  butikk: "i butikk (markert manuelt)",
  ukjent: "",
};

function lesLagretTerminal() {
  try { return localStorage.getItem(TERMINAL_KEY) || ""; } catch { return ""; }
}
function lagreTerminal(id) {
  try { localStorage.setItem(TERMINAL_KEY, id); } catch { /* ignorer */ }
}

export default function BetalService() {
  const { ref } = useParams();
  if (!isAuthenticated()) return <BetalQrInfo />;
  return <BetalServiceAnsatt serviceRef={ref} />;
}

// Det en kunde ser når de skanner lappen. Ingen data hentes.
function BetalQrInfo() {
  const location = useLocation();
  useEffect(() => {
    document.title = "Servicelapp – Røyken Sykkelservice";
  }, []);
  return (
    <div className="max-w-md mx-auto mt-10 text-center">
      <h1 className="text-2xl font-bold mb-3">Denne koden brukes i butikken</h1>
      <p className="text-gray-700 mb-3">
        QR-koden på servicelappen skannes av oss når du betaler i butikken.
      </p>
      <p className="text-gray-700 mb-6">
        Status, pristilbud og betaling for servicen din finner du via lenken du har fått på SMS
        eller e-post.
      </p>
      <a
        href="https://roykensykkelservice.no"
        className="inline-block max-w-full px-4 py-2 rounded bg-emerald-700 text-white font-semibold hover:bg-emerald-800 [overflow-wrap:anywhere]"
      >
        Til roykensykkelservice.no
      </a>
      <p className="mt-10 text-sm">
        <Link to="/login" state={{ from: location }} className="text-gray-700 underline">
          Ansatt? Logg inn
        </Link>
      </p>
    </div>
  );
}

function BetalServiceAnsatt({ serviceRef }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [terminalInfo, setTerminalInfo] = useState({ modus: null, terminaler: [] });
  const [terminalId, setTerminalId] = useState(lesLagretTerminal());
  const [lasterFeil, setLasterFeil] = useState("");
  const [handlingFeil, setHandlingFeil] = useState("");
  const [vippsAdvarsel, setVippsAdvarsel] = useState(false);
  const [simulertUtfall, setSimulertUtfall] = useState("godkjent");
  const [busy, setBusy] = useState(false);

  const dialogRef = useRef(null);
  const headingRef = useRef(null);
  const statusRef = useRef(null);

  const lukk = useCallback(() => navigate("/betal"), [navigate]);

  const hent = useCallback(async () => {
    const r = await fetch(`${API_BASE_URL}/terminal/service/${encodeURIComponent(serviceRef)}`);
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.message || "Kunne ikke hente servicen");
    return body;
  }, [serviceRef]);

  useEffect(() => {
    document.title = `Betaling service #${serviceRef} – Røyken Sykkelservice`;
    let alive = true;
    (async () => {
      try {
        const [svc, info] = await Promise.all([
          hent(),
          fetch(`${API_BASE_URL}/terminal`).then((r) => (r.ok ? r.json() : { modus: null, terminaler: [] })),
        ]);
        if (!alive) return;
        setData(svc);
        setTerminalInfo(info);
        setTerminalId((cur) => {
          if (cur && info.terminaler.some((t) => t.id === cur)) return cur;
          const butikk = Cookies.get("selectedStore");
          return info.terminaler.find((t) => t.butikk && t.butikk === butikk)?.id || info.terminaler[0]?.id || "";
        });
      } catch (e) {
        if (alive) setLasterFeil(e.message);
      }
    })();
    return () => { alive = false; };
  }, [hent, serviceRef]);

  // Poll mens betalingen venter på terminalen.
  const venter = data?.terminal?.status === "venter";
  const erBetalt = !!data?.betaling?.betalt;
  useEffect(() => {
    if (!venter) return;
    const t = setInterval(async () => {
      try { setData(await hent()); } catch { /* prøver igjen */ }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [venter, hent]);

  // Fokus inn i dialogen, Escape lukker, Tab holdes inne i dialogen (U2).
  useEffect(() => {
    if (!data) return;
    headingRef.current?.focus();
  }, [data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Knappen som ble trykket forsvinner når status endres (venter/betalt), og da
  // havner fokus utenfor dialogen. Flytt det til statusfeltet (U2, WCAG 2.4.3).
  useEffect(() => {
    if (!data) return;
    if (dialogRef.current && !dialogRef.current.contains(document.activeElement)) {
      statusRef.current?.focus();
    }
  }, [venter, erBetalt, data?.terminal?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  function onKeyDown(e) {
    if (e.key === "Escape" && !venter) {
      e.stopPropagation();
      lukk();
      return;
    }
    if (e.key !== "Tab" || !dialogRef.current) return;
    const f = dialogRef.current.querySelectorAll(
      'button:not([disabled]), select:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    );
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  async function sendTilTerminal(tillatTrossVipps = false) {
    setBusy(true);
    setHandlingFeil("");
    try {
      lagreTerminal(terminalId);
      const r = await fetch(`${API_BASE_URL}/terminal/service/${encodeURIComponent(serviceRef)}/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          terminalId,
          ansatt: Cookies.get("selectedEmployee") || "",
          tillatTrossVipps,
          simulertUtfall,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (r.status === 409 && body.kode === "vipps_pagar") {
        setVippsAdvarsel(true);
        return;
      }
      if (body.service) setData(body.service);
      if (!r.ok) throw new Error(body.message || "Kunne ikke starte betalingen");
      setVippsAdvarsel(false);
      setData(body);
    } catch (e) {
      setHandlingFeil(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function avbryt() {
    setBusy(true);
    setHandlingFeil("");
    try {
      const r = await fetch(`${API_BASE_URL}/terminal/service/${encodeURIComponent(serviceRef)}/avbryt`, { method: "POST" });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.message || "Kunne ikke avbryte");
      setData(body);
    } catch (e) {
      setHandlingFeil(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (lasterFeil) {
    return (
      <div className="max-w-md mx-auto mt-10">
        <h1 className="text-2xl font-bold mb-3">Service #{serviceRef}</h1>
        <p role="alert" className="text-red-700 mb-4">{lasterFeil}</p>
        <Link to="/betal" className="text-emerald-800 underline">Tilbake til Utfør betaling</Link>
      </div>
    );
  }
  if (!data) {
    return <p className="text-center mt-10 text-gray-700" role="status">Henter service #{serviceRef}…</p>;
  }

  const betalt = data.betaling?.betalt;
  const terminalStatus = data.terminal?.status;
  const tilkoblet = !!terminalInfo.modus;
  const simulator = terminalInfo.modus === "simulator";
  const terminalNavn = terminalInfo.terminaler.find((t) => t.id === data.terminal?.terminalId)?.navn || "terminalen";
  const produkt = [data.produkt?.merke, data.produkt?.type].filter(Boolean).join(" – ");

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-auto">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="betal-tittel"
        onKeyDown={onKeyDown}
        className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[95vh] overflow-auto"
      >
        {/* Topp: betalingsknapp/status */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 rounded-t-lg">
          <div className="flex items-start justify-between gap-3">
            <h1 id="betal-tittel" ref={headingRef} tabIndex={-1} className="text-xl font-bold focus:outline-none">
              Service #{data.serviceRef}
            </h1>
            <button
              type="button"
              onClick={lukk}
              disabled={venter}
              className="px-3 py-1 rounded border border-gray-400 text-gray-800 hover:bg-gray-100 disabled:opacity-50"
            >
              Lukk
            </button>
          </div>

          <div ref={statusRef} tabIndex={-1} aria-live="polite" className="mt-3 focus:outline-none">
            {betalt ? (
              <div className="rounded border border-emerald-700 bg-emerald-50 p-3 text-emerald-900">
                <p className="font-bold text-lg">✓ Allerede betalt</p>
                <p className="text-sm">
                  Betalt {VIA_TEKST[data.betaling.via] || ""}
                  {data.betaling.belop != null ? `, ${fmtKr(data.betaling.belop)}` : ""}
                  {data.betaling.at ? ` – ${fmtDT(data.betaling.at)}` : ""}
                  {data.betaling.simulert ? " (simulert)" : ""}
                </p>
                {data.kvitteringsnummer && <p className="text-sm">Kvitteringsnr. {data.kvitteringsnummer}</p>}
              </div>
            ) : venter ? (
              <div className="rounded border border-amber-600 bg-amber-50 p-3 text-amber-900">
                <p className="font-bold">Venter på kort på {terminalNavn} …</p>
                <p className="text-sm">{fmtKr(data.terminal.belop)} er sendt til terminalen.</p>
                <button
                  type="button"
                  onClick={avbryt}
                  disabled={busy}
                  className="mt-2 px-3 py-1 rounded border border-amber-700 text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                >
                  Avbryt betalingen
                </button>
              </div>
            ) : (
              <>
                {terminalStatus === "avvist" && (
                  <p className="mb-2 text-red-700 font-semibold">Betalingen ble avvist på terminalen. Prøv igjen.</p>
                )}
                {terminalStatus === "avbrutt" && (
                  <p className="mb-2 text-gray-800">Forrige forsøk ble avbrutt.</p>
                )}
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label htmlFor="betal-terminal" className="block text-sm font-medium text-gray-800">Terminal</label>
                    <select
                      id="betal-terminal"
                      value={terminalId}
                      onChange={(e) => setTerminalId(e.target.value)}
                      className="border border-gray-400 rounded px-2 py-2 bg-white"
                    >
                      {terminalInfo.terminaler.map((t) => (
                        <option key={t.id} value={t.id}>{t.navn}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => sendTilTerminal(false)}
                    disabled={busy || !tilkoblet || !terminalId || !(data.belop?.total > 0)}
                    className="px-4 py-2 rounded bg-emerald-700 text-white font-semibold hover:bg-emerald-800 disabled:bg-gray-500 disabled:cursor-not-allowed"
                  >
                    💳 Send {fmtKr(data.belop?.total)} til terminal
                  </button>
                </div>
                {simulator && (
                  <fieldset className="mt-2 text-sm text-gray-800">
                    <legend className="font-medium">Simulator – utfall</legend>
                    <label className="mr-4">
                      <input type="radio" name="simutfall" value="godkjent" checked={simulertUtfall === "godkjent"} onChange={() => setSimulertUtfall("godkjent")} /> Godkjent
                    </label>
                    <label>
                      <input type="radio" name="simutfall" value="avvist" checked={simulertUtfall === "avvist"} onChange={() => setSimulertUtfall("avvist")} /> Avvist
                    </label>
                  </fieldset>
                )}
                {!tilkoblet && (
                  <p className="mt-2 text-sm text-gray-700">
                    Betalingsterminalen er ikke koblet til ennå. Ta betalt på terminalen som før og huk av
                    «Kunden har betalt i butikk» på servicefakturaen.
                  </p>
                )}
                {data.vippsPagar && !vippsAdvarsel && (
                  <p className="mt-2 text-sm text-amber-900">
                    Kunden har startet en Vipps-betaling som ikke er fullført.
                  </p>
                )}
                {vippsAdvarsel && (
                  <div role="alert" className="mt-2 rounded border border-amber-600 bg-amber-50 p-2 text-sm text-amber-900">
                    <p>Kunden har startet en Vipps-betaling som ikke er fullført. Spør kunden før du tar betalt på terminalen.</p>
                    <button
                      type="button"
                      onClick={() => sendTilTerminal(true)}
                      disabled={busy}
                      className="mt-2 px-3 py-1 rounded bg-amber-700 text-white font-semibold hover:bg-amber-800 disabled:opacity-50"
                    >
                      Send til terminal likevel
                    </button>
                  </div>
                )}
              </>
            )}
            {handlingFeil && <p role="alert" className="mt-2 text-red-700">{handlingFeil}</p>}
          </div>
        </div>

        {/* Kvittering */}
        <div className="p-4">
          <div className="text-sm text-gray-800 mb-3">
            <div><span className="font-semibold">Kunde:</span> {data.kundeNavn || "–"}</div>
            {produkt && <div><span className="font-semibold">Produkt:</span> {produkt}{data.produkt.farge ? `, ${data.produkt.farge}` : ""}</div>}
            {data.produkt?.butikk && <div><span className="font-semibold">Butikk:</span> {data.produkt.butikk}</div>}
            <div><span className="font-semibold">Status:</span> {data.status || "–"}</div>
          </div>

          <Linjer
            tittel="Arbeid"
            rader={data.serviceLines.map((l) => ({
              navn: l.navn,
              merknad: l.reklamasjon ? "Reklamasjon" : l.gratisarbeid ? "Gratis" : "",
              belop: l.pris,
              stroket: l.reklamasjon || l.gratisarbeid,
            }))}
          />
          <Linjer
            tittel="Deler"
            rader={data.partsLines.map((p) => ({
              navn: `${p.qty > 1 ? `${p.qty} × ` : ""}${p.navn}`,
              merknad: p.isWarranty ? "Garanti" : p.discount > 0 ? `Rabatt ${fmtKr(p.discount)}` : "",
              belop: Math.max(0, p.price * p.qty - p.discount),
              stroket: p.isWarranty,
            }))}
          />
          <Linjer
            tittel="Frakt"
            rader={data.fraktLines.map((f) => ({ navn: f.beskrivelse, belop: f.pris }))}
          />

          <div className="flex justify-between border-t-2 border-gray-800 pt-2 mt-2 text-lg font-bold">
            <span>Å betale</span>
            <span>{fmtKr(data.belop?.total)}</span>
          </div>
          <p className="text-xs text-gray-700 mt-1">Inkl. 25 % mva.</p>

          <p className="mt-4 text-sm">
            <Link to={`/workshop/${data.id}`} className="text-emerald-800 underline">Åpne servicen</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Linjer({ tittel, rader }) {
  if (!rader.length) return null;
  return (
    <section className="mb-3">
      <h2 className="text-sm font-bold uppercase tracking-wide text-gray-700 mb-1">{tittel}</h2>
      <ul className="divide-y divide-gray-200">
        {rader.map((r, i) => (
          <li key={i} className="flex justify-between gap-3 py-1">
            <span>
              {r.navn}
              {r.merknad && <span className="ml-2 text-xs text-gray-700">({r.merknad})</span>}
            </span>
            <span className={r.stroket ? "line-through text-gray-600" : ""}>{fmtKr(r.belop)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
